import React, { useEffect, useState, useRef } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Typography, Box, CircularProgress } from '@mui/material';
import SignalWifiOffIcon from '@mui/icons-material/SignalWifiOff';
import SignalWifi4BarIcon from '@mui/icons-material/SignalWifi4Bar';
import SignalWifi3BarIcon from '@mui/icons-material/SignalWifi3Bar';
import SignalWifi2BarIcon from '@mui/icons-material/SignalWifi2Bar';
import WarningIcon from '@mui/icons-material/Warning';
import { serverPath } from '../../services/ajax';

const useStyles = makeStyles()((theme) => ({
    container: {
        position: "absolute",
        bottom: 0,
        zIndex: 1200,
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        transition: "all 0.5s ease-in-out",
        overflow: "hidden",
        boxShadow: "0 -2px 8px rgba(0,0,0,0.15)",
    },
    content: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        padding: theme.spacing(1, 2),
        width: "100%",
        maxWidth: "100%",
        justifyContent: "center",
    },
    mensaje: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        fontWeight: 500,
    },
    icon: {
        fontSize: "1.2rem",
    },
}));

// Estados de conexión
const CONNECTION_STATES = {
    ONLINE: 'online',
    OFFLINE: 'offline',
    SLOW: 'slow',
    POOR: 'poor',
    TIMEOUT: 'timeout',
    CHECKING: 'checking',
};

// Configuración de colores y mensajes según el estado
const STATE_CONFIG = {
    [CONNECTION_STATES.OFFLINE]: {
        backgroundColor: '#f44336', // Rojo
        color: '#fff',
        message: 'Sin conexión a internet. Verifica tu conexión de red.',
        icon: SignalWifiOffIcon,
    },
    [CONNECTION_STATES.TIMEOUT]: {
        backgroundColor: '#ff9800', // Naranja
        color: '#fff',
        message: 'El servidor no responde. La conexión puede estar interrumpida.',
        icon: WarningIcon,
    },
    [CONNECTION_STATES.POOR]: {
        backgroundColor: '#ff9800', // Naranja
        color: '#fff',
        message: 'Conexión de muy mala calidad. Las operaciones pueden fallar o ser lentas.',
        icon: SignalWifi2BarIcon,
    },
    [CONNECTION_STATES.SLOW]: {
        backgroundColor: '#FFD801', // Amarillo
        color: '#333',
        message: 'Conexión lenta. Algunas operaciones pueden tardar más de lo normal.',
        icon: SignalWifi3BarIcon,
    },
    [CONNECTION_STATES.CHECKING]: {
        backgroundColor: '#2196f3', // Azul
        color: '#fff',
        message: 'Verificando conexión...',
        icon: null,
    },
    [CONNECTION_STATES.ONLINE]: {
        backgroundColor: 'transparent',
        color: 'transparent',
        message: '',
        icon: SignalWifi4BarIcon,
    },
};

// Umbrales de latencia (en milisegundos)
const LATENCY_THRESHOLDS = {
    EXCELLENT: 100,  // < 100ms: Excelente
    GOOD: 300,       // < 300ms: Buena
    SLOW: 1000,      // < 1000ms: Lenta
    POOR: 3000,      // < 3000ms: Muy mala
    // > 3000ms o timeout: Sin conexión efectiva
};

// Tiempo entre verificaciones según el estado de conexión (en milisegundos)
const CHECK_INTERVALS = {
    GOOD: 60000,      // 1 minuto cuando la conexión está bien
    REGULAR: 30000,   // 30 segundos cuando la conexión está regular
    POOR: 10000,      // 10 segundos cuando no hay conexión
};
const TIMEOUT_DURATION = 5000; // 5 segundos para timeout
const STORAGE_KEY = 'ch-rrhh-srh-connection-state';

// Función para obtener el intervalo de verificación según el estado
const getCheckInterval = (state) => {
    switch (state) {
        case CONNECTION_STATES.ONLINE:
            return CHECK_INTERVALS.GOOD;
        case CONNECTION_STATES.SLOW:
        case CONNECTION_STATES.POOR:
            return CHECK_INTERVALS.REGULAR;
        case CONNECTION_STATES.OFFLINE:
        case CONNECTION_STATES.TIMEOUT:
        default:
            return CHECK_INTERVALS.POOR;
    }
};

// Función para obtener el estado guardado del localStorage
const getStoredState = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored && Object.values(CONNECTION_STATES).includes(stored)) {
            return stored;
        }
    } catch (e) {
        // Si hay error al leer localStorage, ignorar
    }
    return null;
};

// Función para guardar el estado en localStorage
const saveState = (state) => {
    try {
        if (state && state !== CONNECTION_STATES.CHECKING) {
            localStorage.setItem(STORAGE_KEY, state);
        }
    } catch (e) {
        // Si hay error al escribir localStorage, ignorar
    }
};

function ConectionAlert(props) {
    const { classes } = useStyles();
    // Inicializar con el estado guardado o ONLINE si no hay ninguno
    const initialState = getStoredState() || (navigator.onLine ? CONNECTION_STATES.ONLINE : CONNECTION_STATES.OFFLINE);
    const [connectionState, setConnectionState] = useState(initialState);
    const [lastKnownState, setLastKnownState] = useState(initialState !== CONNECTION_STATES.ONLINE ? initialState : null);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [hasChecked, setHasChecked] = useState(false); // Indica si ya se completó la primera verificación
    const checkIntervalRef = useRef(null);
    const abortControllerRef = useRef(null);
    const isCheckingRef = useRef(false); // Ref para saber si está verificando sin cambiar el estado

    // Función para verificar la calidad de la conexión mediante ping
    const checkConnectionQuality = async () => {
        // Si no hay conexión según el navegador, retornar offline
        if (!navigator.onLine) {
            return CONNECTION_STATES.OFFLINE;
        }

        // Obtener la URL base del backend usando la función del proyecto
        const apiBasePath = serverPath();
        // Construir la URL completa para el health check
        let pingUrl;
        if (apiBasePath.startsWith('http')) {
            // Si es una URL completa, reemplazar el path y agregar /health
            pingUrl = apiBasePath.replace('/api/v1/', '/api/v1/health');
        } else {
            // Si es una ruta relativa, construir desde el origen actual
            pingUrl = `${window.location.origin}${apiBasePath}health`;
        }

        try {
            const startTime = performance.now();
            abortControllerRef.current = new AbortController();

            // Crear un timeout manual usando Promise.race
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => {
                    abortControllerRef.current?.abort();
                    reject(new Error('Timeout'));
                }, TIMEOUT_DURATION);
            });

            const fetchPromise = fetch(pingUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
                signal: abortControllerRef.current.signal,
                cache: 'no-cache',
            });

            const response = await Promise.race([fetchPromise, timeoutPromise]);
            const endTime = performance.now();
            const latency = endTime - startTime;

            // Si la respuesta es exitosa (200), evaluar la latencia
            if (response && response.ok) {
                if (latency >= LATENCY_THRESHOLDS.POOR) {
                    return CONNECTION_STATES.POOR;
                } else if (latency >= LATENCY_THRESHOLDS.SLOW) {
                    return CONNECTION_STATES.SLOW;
                } else {
                    return CONNECTION_STATES.ONLINE;
                }
            } else if (response) {
                // Si hay respuesta pero no es exitosa, evaluar por latencia
                // (puede ser un error del servidor pero la conexión funciona)
                return latency >= LATENCY_THRESHOLDS.POOR 
                    ? CONNECTION_STATES.POOR 
                    : CONNECTION_STATES.SLOW;
            } else {
                return CONNECTION_STATES.TIMEOUT;
            }
        } catch (error) {
            // Si es un error de abort (timeout manual), retornar timeout
            if (error.name === 'AbortError' || error.message === 'Timeout') {
                return CONNECTION_STATES.TIMEOUT;
            }

            // Si es timeout o error de red
            if (error.name === 'TimeoutError' || 
                error.message.includes('timeout') || 
                error.message.includes('Failed to fetch') ||
                error.message.includes('NetworkError')) {
                // Verificar si realmente no hay conexión
                if (!navigator.onLine) {
                    return CONNECTION_STATES.OFFLINE;
                }
                return CONNECTION_STATES.TIMEOUT;
            }

            // Si no hay conexión
            if (!navigator.onLine) {
                return CONNECTION_STATES.OFFLINE;
            }

            // Otros errores pueden indicar conexión pobre
            return CONNECTION_STATES.POOR;
        }
    };

    // Función para actualizar el estado de conexión
    const updateConnectionState = async () => {
        // Marcar que estamos verificando (sin cambiar el estado visual)
        isCheckingRef.current = true;
        
        // Primero verificar el estado básico del navegador
        const browserOnline = navigator.onLine;
        setIsOnline(browserOnline);

        if (!browserOnline) {
            const newState = CONNECTION_STATES.OFFLINE;
            setConnectionState(newState);
            setLastKnownState(newState);
            saveState(newState);
            setHasChecked(true); // Marcar que ya se completó la primera verificación
            isCheckingRef.current = false;
            // El useEffect que depende de connectionState reconfigurará el intervalo
            return;
        }

        // Verificar la calidad sin cambiar el estado visual
        // Mantener el estado actual mientras se verifica
        const quality = await checkConnectionQuality();
        
        // Actualizar el estado si cambió (esto disparará el useEffect que reconfigura el intervalo)
        setConnectionState(prevState => {
            if (quality !== prevState) {
                setLastKnownState(quality);
                saveState(quality);
                return quality;
            }
            return prevState;
        });
        
        // Marcar que ya se completó la primera verificación
        setHasChecked(true);
        isCheckingRef.current = false;
    };

    // Manejar cambios en el estado online/offline del navegador
    const handleStatusChange = () => {
        updateConnectionState();
    };

    useEffect(() => {
        // Verificación inicial
        updateConnectionState();

        // Escuchar eventos de online/offline
        window.addEventListener('online', handleStatusChange);
        window.addEventListener('offline', handleStatusChange);

        // Cleanup
        return () => {
            window.removeEventListener('online', handleStatusChange);
            window.removeEventListener('offline', handleStatusChange);
            
            if (checkIntervalRef.current) {
                clearInterval(checkIntervalRef.current);
            }
            
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    // Reconfigurar el intervalo cuando cambia el estado de conexión
    useEffect(() => {
        // Limpiar intervalo anterior si existe
        if (checkIntervalRef.current) {
            clearInterval(checkIntervalRef.current);
        }
        
        // Obtener el intervalo según el estado actual de conexión
        const interval = getCheckInterval(connectionState);
        
        // Configurar nuevo intervalo
        checkIntervalRef.current = setInterval(() => {
            updateConnectionState();
        }, interval);
        
        // Cleanup del intervalo cuando el componente se desmonte o cambie el estado
        return () => {
            if (checkIntervalRef.current) {
                clearInterval(checkIntervalRef.current);
            }
        };
    }, [connectionState]);

    // Nunca mostrar CHECKING, usar el estado actual o el último conocido
    const displayState = connectionState === CONNECTION_STATES.CHECKING 
        ? (lastKnownState || CONNECTION_STATES.ONLINE)
        : connectionState;
    
    const config = STATE_CONFIG[displayState];
    const IconComponent = config.icon;
    // Solo mostrar si ya se completó la primera verificación Y el estado no es ONLINE
    const shouldShow = hasChecked && displayState !== CONNECTION_STATES.ONLINE;

    return (
        <div
            className={classes.container}
            style={{
                height: shouldShow ? "auto" : 0,
                opacity: shouldShow ? 1 : 0,
                backgroundColor: config.backgroundColor,
                color: config.color,
            }}
        >
            {shouldShow && (
                <Box className={classes.content}>
                    {connectionState === CONNECTION_STATES.CHECKING ? (
                        <>
                            <CircularProgress size={16} sx={{ color: config.color }} />
                            <Typography variant="body2" className={classes.mensaje}>
                                {config.message}
                            </Typography>
                        </>
                    ) : (
                        <>
                            {IconComponent && (
                                <IconComponent className={classes.icon} />
                            )}
                            <Typography variant="body2" className={classes.mensaje}>
                                {config.message}
                            </Typography>
                        </>
                    )}
                </Box>
            )}
        </div>
    );
}

export default ConectionAlert;
